import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './LegalPage.css';

const sections = [
  { id: 's1',  label: 'Grant of License' },
  { id: 's2',  label: 'Rights and Limitations' },
  { id: 's3',  label: 'Title to Software' },
  { id: 's4',  label: 'Intellectual Property' },
  { id: 's5',  label: 'No Support' },
  { id: 's6',  label: 'Duration & Termination' },
  { id: 's7',  label: 'Jurisdiction' },
  { id: 's8',  label: 'Non-Transferable' },
  { id: 's9',  label: 'Severability' },
  { id: 's10', label: 'Warranty Disclaimer' },
  { id: 's11', label: 'Limitation of Liability' },
  { id: 's12', label: 'Entire Agreement' },
];

export default function TermsOfService() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Terms of Service — PictureIt';
  }, []);

  return (
    <div className="legal-page">
      {/* Hero */}
      <div className="legal-hero">
        <div className="legal-hero-badge">Legal</div>
        <h1>Terms of Service</h1>
        <div className="legal-hero-meta">
          <span>End User License Agreement · Rev. 133EF48</span>
          <span>State of Maryland</span>
        </div>
      </div>

      <div className="legal-layout">
        {/* Sidebar */}
        <aside className="legal-sidebar">
          <h3>On This Page</h3>
          <nav>
            {sections.map((s) => (
              <a key={s.id} href={`#${s.id}`}>{s.label}</a>
            ))}
          </nav>
          <Link to="/" className="legal-sidebar-back">← Back to Home</Link>
        </aside>

        {/* Main content */}
        <main className="legal-content">
          <p className="legal-intro">
            This End-User License Agreement (<strong>"EULA"</strong>) is a legal agreement between you
            (<strong>"Licensee"</strong>) and Pictureit (<strong>"Licensor"</strong>), the author of
            Pictureit, including all HTML files, XML files, Java files, graphics files, animation files,
            data files, technology, development tools, scripts and programs, both in object code and source
            code (the <strong>"Software"</strong>).
            <br /><br />
            By installing, copying, or otherwise using the Software, Licensee agrees to be bound by the
            terms and conditions set forth in this EULA. If you do not agree, you may not download, install,
            or use the Software.
          </p>

          {/* Section 1 */}
          <div className="legal-section" id="s1">
            <div className="legal-section-header">
              <span className="legal-section-number">1</span>
              <h2>Grant of License</h2>
            </div>
            <h3>A) Scope of License</h3>
            <p>
              Subject to the terms of this EULA, Licensor hereby grants to Licensee a royalty-free,
              non-exclusive license to possess and to use a copy of the Software.
            </p>
            <h3>B) Installation and Use</h3>
            <p>
              Licensee may install and use an unlimited number of copies of the Software solely for
              Licensee's personal use.
            </p>
          </div>

          <div className="legal-divider" />

          {/* Section 2 */}
          <div className="legal-section" id="s2">
            <div className="legal-section-header">
              <span className="legal-section-number">2</span>
              <h2>Description of Rights and Limitations</h2>
            </div>
            <h3>A) Limitations</h3>
            <p>
              Licensee and third parties may not reverse engineer, decompile, or disassemble the Software,
              except and only to the extent that such activity is expressly permitted by applicable law
              notwithstanding the limitation.
            </p>
            <h3>B) Update and Maintenance</h3>
            <p>
              Licensor shall provide updates and maintenance on the Software on an as-needed basis.
            </p>
            <h3>C) Separation of Components</h3>
            <p>
              The Software is licensed as a single product. Its components may not be separated for use on
              more than one computer.
            </p>
          </div>

          <div className="legal-divider" />

          {/* Section 3 */}
          <div className="legal-section" id="s3">
            <div className="legal-section-header">
              <span className="legal-section-number">3</span>
              <h2>Title to Software</h2>
            </div>
            <p>
              Licensor represents and warrants that it has the legal right to enter into and perform its
              obligations under this EULA, and that use by the Licensee of the Software, in accordance with
              the terms of this EULA, will not infringe upon the intellectual property rights of any third
              parties.
            </p>
          </div>

          <div className="legal-divider" />

          {/* Section 4 */}
          <div className="legal-section" id="s4">
            <div className="legal-section-header">
              <span className="legal-section-number">4</span>
              <h2>Intellectual Property</h2>
            </div>
            <p>
              All now known or hereafter known tangible and intangible rights, title, interest, copyrights
              and moral rights in and to the Software, including but not limited to all images, photographs,
              animations, video, audio, music, text, data, computer code, algorithms, and information, are
              owned by Licensor. The Software is protected by all applicable copyright laws and international
              treaties.
            </p>
          </div>

          <div className="legal-divider" />

          {/* Section 5 */}
          <div className="legal-section" id="s5">
            <div className="legal-section-header">
              <span className="legal-section-number">5</span>
              <h2>No Support</h2>
            </div>
            <p>
              Licensor has no obligation to provide support services for the Software.
            </p>
          </div>

          <div className="legal-divider" />

          {/* Section 6 */}
          <div className="legal-section" id="s6">
            <div className="legal-section-header">
              <span className="legal-section-number">6</span>
              <h2>Duration &amp; Termination</h2>
            </div>
            <p>This EULA is perpetual or until:</p>
            <ul>
              <li>
                <strong>A) Automatic Termination</strong> — automatically terminated or suspended if
                Licensee fails to comply with any of the terms and conditions set forth in this EULA.
              </li>
              <li>
                <strong>B) Licensor Termination</strong> — terminated or suspended by Licensor, with or
                without cause.
              </li>
            </ul>
            <p>
              In the event this EULA is terminated, you must cease use of the Software and destroy all
              copies of the Software.
            </p>
          </div>

          <div className="legal-divider" />

          {/* Section 7 */}
          <div className="legal-section" id="s7">
            <div className="legal-section-header">
              <span className="legal-section-number">7</span>
              <h2>Jurisdiction</h2>
            </div>
            <p>
              This EULA shall be deemed to have been made in, and shall be construed pursuant to the laws of
              the <strong>State of Maryland</strong>, without regard to conflicts of laws provisions thereof.
              Any legal action or proceeding relating to this EULA shall be brought exclusively in courts
              located in <strong>Silver Spring, MD</strong>, and each party consents to the jurisdiction
              thereof.
            </p>
            <p>
              The prevailing party in any action to enforce this EULA shall be entitled to recover costs and
              expenses including, without limitation, attorneys' fees. This EULA is made within the exclusive
              jurisdiction of the United States.
            </p>
          </div>

          <div className="legal-divider" />

          {/* Section 8 */}
          <div className="legal-section" id="s8">
            <div className="legal-section-header">
              <span className="legal-section-number">8</span>
              <h2>Non-Transferable</h2>
            </div>
            <p>
              This EULA is not assignable or transferable by Licensee, and any attempt to do so would be
              void.
            </p>
          </div>

          <div className="legal-divider" />

          {/* Section 9 */}
          <div className="legal-section" id="s9">
            <div className="legal-section-header">
              <span className="legal-section-number">9</span>
              <h2>Severability</h2>
            </div>
            <p>
              No failure to exercise, and no delay in exercising, on the part of either party, any privilege,
              any power or any rights hereunder will operate as a waiver thereof, nor will any single or
              partial exercise of any right or power hereunder preclude further exercise of any other right
              hereunder.
            </p>
            <p>
              If any provision of this EULA shall be adjudged by any court of competent jurisdiction to be
              unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent
              necessary so that this EULA shall otherwise remain in full force and effect and enforceable.
            </p>
          </div>

          <div className="legal-divider" />

          {/* Section 10 */}
          <div className="legal-section" id="s10">
            <div className="legal-section-header">
              <span className="legal-section-number">10</span>
              <h2>Warranty Disclaimer</h2>
            </div>
            <div className="legal-warning">
              <p>
                LICENSOR, AND AUTHOR OF THE SOFTWARE, HEREBY EXPRESSLY DISCLAIM ANY WARRANTY FOR THE
                SOFTWARE. THE SOFTWARE AND ANY RELATED DOCUMENTATION IS PROVIDED "AS IS" WITHOUT WARRANTY
                OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING, WITHOUT LIMITATION, THE IMPLIED
                WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
                LICENSEE ACCEPTS ANY AND ALL RISK ARISING OUT OF USE OR PERFORMANCE OF THE SOFTWARE.
              </p>
            </div>
          </div>

          <div className="legal-divider" />

          {/* Section 11 */}
          <div className="legal-section" id="s11">
            <div className="legal-section-header">
              <span className="legal-section-number">11</span>
              <h2>Limitation of Liability</h2>
            </div>
            <div className="legal-warning">
              <p>
                LICENSOR SHALL NOT BE LIABLE TO LICENSEE, OR ANY OTHER PERSON OR ENTITY CLAIMING THROUGH
                LICENSEE ANY LOSS OF PROFITS, INCOME, SAVINGS, OR ANY OTHER CONSEQUENTIAL, INCIDENTAL,
                SPECIAL, PUNITIVE, DIRECT OR INDIRECT DAMAGE, WHETHER ARISING IN CONTRACT, TORT, WARRANTY,
                OR OTHERWISE. THESE LIMITATIONS SHALL APPLY REGARDLESS OF THE ESSENTIAL PURPOSE OF ANY
                LIMITED REMEDY. UNDER NO CIRCUMSTANCES SHALL LICENSOR'S AGGREGATE LIABILITY TO LICENSEE,
                OR ANY OTHER PERSON OR ENTITY CLAIMING THROUGH LICENSEE, EXCEED THE FINANCIAL AMOUNT
                ACTUALLY PAID BY LICENSEE TO LICENSOR FOR THE SOFTWARE.
              </p>
            </div>
          </div>

          <div className="legal-divider" />

          {/* Section 12 */}
          <div className="legal-section" id="s12">
            <div className="legal-section-header">
              <span className="legal-section-number">12</span>
              <h2>Entire Agreement</h2>
            </div>
            <p>
              This EULA constitutes the entire agreement between Licensor and Licensee and supersedes all
              prior understandings of Licensor and Licensee, including any prior representation, statement,
              condition, or warranty with respect to the subject matter of this EULA.
            </p>

            <div className="legal-contact-box" style={{ marginTop: '28px' }}>
              <p>Questions about these terms?</p>
              <a href="mailto:pictureitteam@gmail.com">pictureitteam@gmail.com</a>
            </div>
          </div>
        </main>
      </div>

      <div className="legal-footer-strip">
        <span>© {new Date().getFullYear()} PictureIt. All rights reserved.</span>
        <Link to="/privacy-policy">Privacy Policy</Link>
        <Link to="/terms-of-service">Terms of Service</Link>
        <Link to="/">Home</Link>
      </div>
    </div>
  );
}
